import { f7 } from 'framework7-react';
import { useCallback, useMemo, useState } from 'react';
import { usePostRepairedPickUpReserve } from './hook/api/usePostRepairedPickUpReserve';
import { useStorageItem } from './hook/useStorageItem';
import { PickUpReserveDialogCard } from './repairPickUpConfirm/pickUpReserveDialogCard';
import { RepairPickUpConfirm } from './repairPickUpConfirm/repairPickUpConfirm';
import { PageTemplateComponent } from './util/common/pageTemplate/pageTemplate';
import { DialogComponent } from './util/dialogFrame';
import { paths } from '@/config/paths';

export const RepairPickUpConfirmPage = () => {
  const { stored } = useStorageItem<{
    carName: string;
    affiliateID: number;
    customerID: number;
    inspectionID: number;
    bodyPaintID: number;
    firstPreferred: Date;
    secondPreferred?: Date;
    thirdPreferred?: Date;
  }>({ key: 'repairPickUpConfirm' });

  const [openReserveDialog, setOpenReserveDialog] = useState(false);

  const getDateText = (date?: Date) => {
    const dateInfo = new Date(date ?? 'undefined');
    if (dateInfo instanceof Date && !isNaN(dateInfo.getTime())) {
      const offset = 1;
      const zeroPad = 2;
      return `
      ${dateInfo.getFullYear()}/
      ${(dateInfo.getMonth() + offset).toString().padStart(zeroPad, '0')}/
      ${dateInfo.getDate().toString().padStart(zeroPad, '0')}
      `;
    } else {
      return undefined;
    }
  };
  const getTimeText = (date?: Date) => {
    const timeInfo = new Date(date ?? 'undefined');
    if (timeInfo instanceof Date && !isNaN(timeInfo.getTime())) {
      const zeroPad = 2;
      return `
        ${timeInfo.getHours().toString().padStart(zeroPad, '0')}:
        ${timeInfo.getMinutes().toString().padStart(zeroPad, '0')}
      `;
    } else {
      return undefined;
    }
  };
  const getDateTimeInfo = (date?: Date) => {
    const dateTimeInfo = new Date(date ?? 'undefined');
    if (dateTimeInfo instanceof Date && !isNaN(dateTimeInfo.getTime())) {
      return dateTimeInfo;
    } else {
      return undefined;
    }
  };

  const reserveConfirmItemList = [
    { label: '対象車両', value: stored?.carName ?? '-' },
    { label: '返却第一希望日', value: getDateText(stored?.firstPreferred) },
    { label: '返却希望時間', value: getTimeText(stored?.firstPreferred) },
    { label: '返却第二希望日', value: getDateText(stored?.secondPreferred) },
    { label: '返却希望時間', value: getTimeText(stored?.secondPreferred) },
    { label: '返却第三希望日', value: getDateText(stored?.thirdPreferred) },
    { label: '返却希望時間', value: getTimeText(stored?.thirdPreferred) },
  ].reduce((list, item) => {
    if (item.value !== undefined) {
      list.push({ label: item.label, value: item.value });
    }
    return list;
  }, [] as { label: string; value: string }[]);

  const reserveInfo = useMemo(() => {
    const JST = 9;
    const firstPreferred = getDateTimeInfo(stored?.firstPreferred);
    const secondPreferred = getDateTimeInfo(stored?.secondPreferred);
    const thirdPreferred = getDateTimeInfo(stored?.thirdPreferred);
    if (firstPreferred) {
      firstPreferred.setHours(firstPreferred.getHours() + JST);
    }
    if (secondPreferred) {
      secondPreferred.setHours(secondPreferred.getHours() + JST);
    }
    if (thirdPreferred) {
      thirdPreferred.setHours(thirdPreferred.getHours() + JST);
    }
    return {
      m_affiliate_id: stored?.affiliateID ?? 0,
      m_customer_id: stored?.customerID ?? 0,
      t_pm_car_inspection_id: stored?.inspectionID ?? 0,
      t_body_paint_id: stored?.bodyPaintID ?? 0,
      no1_pref_date_time: firstPreferred ?? new Date('Invalid Date'),
      no2_pref_date_time: secondPreferred,
      no3_pref_date_time: thirdPreferred,
    };
  }, [stored]);

  const { postRequest: pickUpReserveRequest } = usePostRepairedPickUpReserve({
    info: reserveInfo,
  });

  const handleClickBack = () => {
    f7.views.main.router.navigate(paths.bpsRepairPickUpReserve);
  };

  const handleClickCheckInReserve = useCallback(() => {
    pickUpReserveRequest().then(() => setOpenReserveDialog(true));
  }, [pickUpReserveRequest]);
  const handleClickCloseDialog = useCallback(() => {
    setOpenReserveDialog(false);
    f7.views.main.router.navigate(paths.bpsRepairInfoDetail);
  }, []);

  return (
    <PageTemplateComponent
      pageName={'pickUpReserve'}
      title={'依頼確認'}
      onBack={handleClickBack}
    >
      <RepairPickUpConfirm
        onClick={handleClickCheckInReserve}
        repairItemInfo={reserveConfirmItemList}
      />
      <DialogComponent open={openReserveDialog}>
        <PickUpReserveDialogCard onClick={handleClickCloseDialog} />
      </DialogComponent>
    </PageTemplateComponent>
  );
};
